import React, { createContext, useContext, useEffect, useState, useRef, useCallback, ReactNode } from 'react';
import { detectarIdioma, traducir } from '../i18n/idioma';
import type { Clave } from '../i18n/textos';
import { useIdioma } from '../i18n/idioma';
import { useAuth } from './AuthContext';
import { useWorkspace } from './WorkspaceContext';
import { BusinessSettings, BusinessMetrics, Employee, SettingsRevision } from '../types';
import { fetchBusinessSettings, saveBusinessSettings } from '../services/database';
import { calculateBusinessMetrics } from '../services/metrics';
import { normalizeStoredUploadUrl } from '../services/urlNormalize';

// Re-export so existing call sites keep working without churn.
export { calculateBusinessMetrics };

/**
 * Migrate legacy employee records into the two-factor utilization shape.
 *
 * Legacy:  utilizationPercent (one number, defaulted to 85)
 * New:     availablePercent × billablePercent (two clearer levers)
 *
 * To preserve math exactly for existing tenants, we set available=100 and
 * billable=utilizationPercent, so the product equals the legacy value.
 */
function migrateEmployees(raw: any[]): Employee[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((e): Employee => {
    // Already migrated — leave alone.
    if (typeof e.availablePercent === 'number' && typeof e.billablePercent === 'number') {
      return {
        ...e,
        isBillable: typeof e.isBillable === 'boolean' ? e.isBillable : true,
      };
    }
    const legacyUtil = typeof e.utilizationPercent === 'number' ? e.utilizationPercent : 85;
    return {
      ...e,
      availablePercent: 100,
      billablePercent: legacyUtil,
      // Keep legacy field around so older clients still work — metrics.ts ignores it
      // when the two-factor fields are present.
      utilizationPercent: legacyUtil,
      isBillable: typeof e.isBillable === 'boolean' ? e.isBillable : true,
    };
  });
}

/**
 * Estos valores no son etiquetas de la interfaz: son CONTENIDO SEMILLA. Se
 * copian a los ajustes del contratista la primera vez y a partir de ahí son
 * suyos — los edita y se guardan en la base.
 *
 * Por eso se resuelven UNA VEZ, con `traducir` (función pura) y no con el hook:
 *   - un hook no puede vivir aquí, a nivel de módulo;
 *   - y aunque pudiera, no querríamos que cambiar de idioma reescribiera los
 *     términos del contrato que el contratista ya ajustó a su gusto.
 * Se siembra en el idioma con el que abre la app, y de ahí en adelante manda él.
 */
const IDIOMA_SEMILLA = detectarIdioma();
const sem = (clave: Clave) => traducir(IDIOMA_SEMILLA, clave);

const DEFAULT_SETTINGS: BusinessSettings = {
    profile: { businessName: '', currency: 'USD', serviceArea: '', industryType: 'landscaping', businessDescription: '' },
    season: { weeksPerYear: 35, daysPerWeek: 5, hoursPerDay: 8 },
    employees: [],
    overhead: [],
    pricing: { targetMarginPercent: 20, defaultMaterialMarkupPercent: 30, taxLaborDefault: false },
    rules: { minServiceCall: 50, minHours: 1, tripCharge: 15, emergencySurchargePercent: 50 },
    defaultTerms: sem('ajustes.terminosPorDefecto'),
    warranties: [
        { id: 'standard', name: sem('ajustes.garantia.nombre'), text: sem('ajustes.garantia.texto') }
    ],
    defaultEmailSubject: sem('ajustes.correo.asunto'),
    defaultEmailBody: sem('ajustes.correo.cuerpo'),
    useHtmlEmail: false,
    defaultHtmlBody: '',
    isSetupComplete: false,
    contracts: []
};


interface SettingsContextType {
    settings: BusinessSettings;
    metrics: BusinessMetrics;
    settingsLoading: boolean;
    loadFailed: boolean;
    isDirty: boolean;
    isSaving: boolean;
    updateSettings: (settings: BusinessSettings) => void;
    saveSettings: () => Promise<void>;
    completeSetup: () => Promise<void>;
    retryLoad: () => Promise<void>;
}

/**
 * Los ajustes se guardaron, se releyeron, y el break-even que devolvió la base
 * NO coincide con el que se calculó aquí.
 *
 * Es una clase y no un `new Error(texto)` porque el texto va traducido y quien
 * lo atrapa tiene que reconocerlo sin leerlo.
 */
export class SaveGuardDivergenceError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'SaveGuardDivergenceError';
    }
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const useSettings = () => {
    const context = useContext(SettingsContext);
    if (!context) {
        throw new Error('useSettings must be used within a SettingsProvider');
    }
    return context;
};

export const SettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { t } = useIdioma();
    const { isAuthorized, currentUser } = useAuth();
    const { activeWorkspace, loading: workspaceLoading } = useWorkspace();

    const [settings, setSettingsState] = useState<BusinessSettings>(DEFAULT_SETTINGS);
    const [settingsLoading, setSettingsLoading] = useState(true);
    const [metrics, setMetrics] = useState<BusinessMetrics>(calculateBusinessMetrics(DEFAULT_SETTINGS));
    const [isDirty, setIsDirty] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    // Distinguishes a FAILED settings load (network / multi-row) from a
    // genuinely-absent row. While true, saves are refused so the Setup Wizard
    // can't persist DEFAULT_SETTINGS over a tenant's real data.
    const [loadFailed, setLoadFailed] = useState(false);

    // Keep a ref to the latest settings for the save function
    const settingsRef = useRef<BusinessSettings>(settings);
    // The workspace whose data is currently loaded into settingsRef. Saves are
    // pinned to THIS id (not live localStorage) so a cross-tab workspace switch
    // can't redirect a settings write to the wrong tenant.
    const loadedWsIdRef = useRef<string | undefined>(undefined);

    const loadSettings = useCallback(async (): Promise<boolean> => {
        if (!isAuthorized || !currentUser || workspaceLoading) {
            return false;
        }
        const wsId = activeWorkspace?.id;
        setSettingsLoading(true);
        try {
            const remote = await fetchBusinessSettings(wsId);
            // Reached the DB successfully. Pin the workspace and clear any prior
            // failure. A null `remote` is a legitimate new tenant (no row yet) —
            // keep DEFAULT_SETTINGS so the wizard opens correctly.
            loadedWsIdRef.current = wsId;
            setLoadFailed(false);
            if (remote) {
                const mergedProfile = { ...DEFAULT_SETTINGS.profile, ...(remote.profile || {}) };
                // Heal a legacy 1-hour signed logo URL into its permanent public
                // form on READ only (no DB write) so the logo stops vanishing
                // after its TTL. Already-public / empty URLs pass through untouched.
                if (mergedProfile.logoUrl) {
                    mergedProfile.logoUrl = normalizeStoredUploadUrl(mergedProfile.logoUrl);
                }
                const merged: BusinessSettings = {
                    ...DEFAULT_SETTINGS,   // Base: fill all gaps with defaults
                    ...remote,             // Override: everything from DB wins
                    // Deep-merge nested objects so partial DB data doesn't wipe defaults
                    profile: mergedProfile,
                    season: { ...DEFAULT_SETTINGS.season, ...(remote.season || {}) },
                    pricing: { ...DEFAULT_SETTINGS.pricing, ...(remote.pricing || {}) },
                    rules: { ...DEFAULT_SETTINGS.rules, ...(remote.rules || {}) },
                    // One-time migration of legacy employee records.
                    employees: migrateEmployees(remote.employees ?? []),
                };
                setSettingsState(merged);
                settingsRef.current = merged;
                setMetrics(calculateBusinessMetrics(merged));
            }
            return true;
        } catch (err) {
            // Load FAILED — do NOT leave defaults in a state saveSettings would
            // persist over real data. Mark loadFailed; the app shows a retry.
            console.error('[SettingsContext] Failed to fetch settings:', err);
            setLoadFailed(true);
            return false;
        } finally {
            setSettingsLoading(false);
        }
    }, [isAuthorized, currentUser?.uid, workspaceLoading, activeWorkspace?.id]);

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    const retryLoad = useCallback(async () => { await loadSettings(); }, [loadSettings]);

    // updateSettings: LOCAL ONLY — updates React state, marks dirty, zero DB writes
    const updateSettings = useCallback((newSettings: BusinessSettings) => {
        setSettingsState(newSettings);
        settingsRef.current = newSettings;
        setMetrics(calculateBusinessMetrics(newSettings));
        setIsDirty(true);
    }, []);

    /** Builds a SettingsRevision capturing the derived metrics at save time.
     *  Kept light — just enough to spot "when did break-even jump from X to Y?". */
    const buildRevision = (next: BusinessSettings): SettingsRevision => {
        const m = calculateBusinessMetrics(next);
        return {
            ts: new Date().toISOString(),
            userEmail: currentUser?.email ?? undefined,
            derivedMetrics: {
                breakEvenRate: m.breakEvenRate ?? 0,
                overheadPerManHour: m.overheadPerManHour,
                targetHourlyRate: m.targetHourlyRate,
                totalOverhead: m.totalOverhead ?? 0,
                totalBillableHours: m.totalBillableHours ?? 0,
            },
        };
    };

    /** Saves the given settings, appending a revision record (capped at 50)
     *  and verifying that the round-tripped derived metrics match what we
     *  computed locally. If they diverge, throw so the UI can warn instead
     *  of silently marking clean. */
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const persistWithGuard = async (next: BusinessSettings) => {
        const revision = buildRevision(next);
        const revisions = [...(next.revisions ?? []), revision].slice(-50);
        const toSave: BusinessSettings = { ...next, revisions, schemaVersion: 2 };

        // Persist (pinned to the workspace whose data we actually loaded)
        await saveBusinessSettings(toSave, loadedWsIdRef.current);

        // Verify: refetch and compare break-even
        try {
            const refetched = await fetchBusinessSettings(loadedWsIdRef.current);
            if (refetched) {
                const reverify = calculateBusinessMetrics(refetched as BusinessSettings);
                const localBE = revision.derivedMetrics.breakEvenRate;
                const remoteBE = reverify.breakEvenRate ?? 0;
                if (Math.abs(localBE - remoteBE) > 0.01) {
                    console.warn('[SettingsContext] Save-guard divergence:', { localBE, remoteBE });
                    throw new SaveGuardDivergenceError(
                        t('ajustes.error.divergencia', { remoto: remoteBE.toFixed(2), local: localBE.toFixed(2) })
                    );
                }
            }
        } catch (verifyErr) {
            /* Se re-lanza por TIPO, no por texto.
             *
             * Antes se comparaba `verifyErr.message.includes('Save-guard
             * divergence')` contra un mensaje que sale de `t(...)`, o sea
             * traducido al español: la comparación era siempre falsa, la
             * divergencia caía en el `console.warn` de abajo y los ajustes se
             * marcaban como guardados. Si Supabase se quedaba con otros
             * números, nadie se enteraba nunca. El texto de la interfaz no es
             * un canal de control de flujo. */
            if (verifyErr instanceof SaveGuardDivergenceError) throw verifyErr;
            console.warn('[SettingsContext] Save-guard verification skipped:', verifyErr);
        }

        // Update local ref with the persisted shape so future saves see the new revisions array.
        settingsRef.current = toSave;
        setSettingsState(toSave);
        setMetrics(calculateBusinessMetrics(toSave));
    };

    // saveSettings: Explicit DB persist — called by the Save button
    const saveSettings = useCallback(async () => {
        if (!currentUser) return;
        // Refuse to write over real data we never successfully loaded. Retry the
        // load first; if it recovers, ask the user to review the freshly-loaded
        // settings and save again (their edits were made against defaults).
        if (loadFailed) {
            const ok = await loadSettings();
            if (!ok) throw new Error(sem('ajustes.error.noCargan'));
            throw new Error(sem('ajustes.error.recargados'));
        }
        setIsSaving(true);
        try {
            await persistWithGuard(settingsRef.current);
            setIsDirty(false);
        } catch (err) {
            console.error('[SettingsContext] Failed to save settings:', err);
            throw err; // Let the UI handle the error toast
        } finally {
            setIsSaving(false);
        }
    }, [currentUser, activeWorkspace?.id, loadFailed, loadSettings]);

    const completeSetup = async () => {
        // Same guard as saveSettings — never finalize setup by writing defaults
        // over a tenant whose real settings failed to load.
        if (loadFailed) {
            const ok = await loadSettings();
            if (!ok) throw new Error(sem('ajustes.error.noCargan'));
            throw new Error(sem('ajustes.error.recargados'));
        }
        const newSettings = { ...settings, isSetupComplete: true };
        setSettingsState(newSettings);
        settingsRef.current = newSettings;
        setMetrics(calculateBusinessMetrics(newSettings));
        setIsSaving(true);
        try {
            await persistWithGuard(newSettings);
            setIsDirty(false);
        } catch (err) {
            console.error('[SettingsContext] Failed to complete setup:', err);
            throw err;
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <SettingsContext.Provider value={{ settings, metrics, settingsLoading, loadFailed, isDirty, isSaving, updateSettings, saveSettings, completeSetup, retryLoad }}>
            {children}
        </SettingsContext.Provider>
    );
};
